import React from 'react';
import { Badge } from 'react-bootstrap';
import { FaSortUp, FaSortDown, FaSort } from 'react-icons/fa';

/**
 * SortableHeader - A reusable table header component with sort indicators
 *
 * Features:
 * - Click to sort (ascending first)
 * - Click again to toggle direction
 * - Shift+click to add secondary sort
 * - Shows sort direction and priority number for multi-sort
 *
 * @param {string} field - The field name to sort by
 * @param {Array} sortConfig - Current sort configuration array
 * @param {Function} onSort - Handler called with (field, event) on click
 * @param {Object} style - Additional styles for the th element
 * @param {string} className - Additional class names
 * @param {string} as - Element type to render ('th' by default, use 'span' when nested inside a <th>)
 * @param {React.ReactNode} children - Header content
 */
const SortableHeader = ({
  field,
  sortConfig = [],
  onSort,
  style = {},
  className = '',
  as: Component = 'th',
  children
}) => {
  // Get sort indicator for this field
  const getSortIndicator = () => {
    const index = sortConfig.findIndex(s => s.field === field);
    if (index < 0) return null;
    return {
      index: index + 1,
      direction: sortConfig[index].direction
    };
  };

  const indicator = getSortIndicator();

  return (
    <Component
      style={{
        ...style,
        cursor: 'pointer',
        userSelect: 'none',
        whiteSpace: 'nowrap'
      }}
      className={className}
      onClick={(e) => onSort(field, e)}
      title={`Click to sort${sortConfig.length > 0 ? ', Shift+click to add secondary sort' : ''}`}
    >
      <span className="d-flex align-items-center justify-content-center gap-1">
        {children}
        {indicator ? (
          <span className="d-flex align-items-center">
            {indicator.direction === 'asc' ? (
              <FaSortUp style={{ fontSize: '0.75rem' }} />
            ) : (
              <FaSortDown style={{ fontSize: '0.75rem' }} />
            )}
            {sortConfig.length > 1 && (
              <Badge bg="light" text="dark" style={{ fontSize: '0.6rem', marginLeft: '2px' }}>
                {indicator.index}
              </Badge>
            )}
          </span>
        ) : (
          <FaSort style={{ fontSize: '0.65rem', opacity: 0.4 }} />
        )}
      </span>
    </Component>
  );
};

export default SortableHeader;
